import { Input } from "@twilio-paste/core/input";
import { Label } from "@twilio-paste/core/label";
import { HelpText } from "@twilio-paste/core/help-text";
import { Box } from "@twilio-paste/core/box";
import { FormEvent } from "react";
import { Button } from "@twilio-paste/core/button";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "@twilio-paste/core/text";
import { ArrowBackIcon } from "@twilio-paste/icons/esm/ArrowBackIcon";

import { sessionDataHandler } from "../sessionDataHandler";
import {
    addNotification,
    changeEngagementPhase,
    updatePreEngagementData,
    updatePreEngagementStep
} from "../store/actions/genericActions";
import { initSession } from "../store/actions/initActions";
import { AppState, EngagementPhase } from "../store/definitions";
import { Header } from "./Header";
import { notifications } from "../notifications";
import { NotificationBar } from "./NotificationBar";
import { isValidCpf, maskCpf } from "../utils/validateCpf";
import { BRAND } from "../brands";
import {
    introStyles,
    fieldStyles,
    titleStyles,
    formStyles,
    cardsContainerStyles,
    cardStyles,
    cardTitleStyles,
    cardSubtitleStyles,
    buttonContainerStyles,
    backButtonStyles
} from "./styles/PreEngagementFormPhase.styles";

const HELP_CENTER_URL = BRAND.helpCenterUrl;

export const PreEngagementFormPhase = () => {
    const { name, cpf } = useSelector((state: AppState) => state.session.preEngagementData) || {};
    const step = useSelector((state: AppState) => state.session.preEngagementStep);
    const dispatch = useDispatch();

    /*
     * Erro inline do CPF não "pipoca" a cada tecla: só quando os 11 dígitos foram
     * digitados e o número é inválido.
     */
    const cpfComplete = (cpf || "").replace(/\D/g, "").length === 11;
    const cpfError = cpfComplete && !isValidCpf(cpf || "");
    const isFormValid = Boolean(name?.trim()) && isValidCpf(cpf || "");

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!isFormValid) {
            return;
        }
        dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
        try {
            const data = await sessionDataHandler.fetchAndStoreNewSession({
                formData: {
                    friendlyName: name,
                    cpf
                }
            });
            dispatch(initSession({ token: data.token, conversationSid: data.conversationSid }));
        } catch (err) {
            dispatch(addNotification(notifications.failedToInitSessionNotification((err as Error).message)));
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        }
    };

    return (
        <>
            <Header />
            <NotificationBar />
            {step === 1 ? (
                <Box {...formStyles} data-test="pre-engagement-welcome">
                    <Text {...titleStyles} as="h3">
                        Seja bem-vindo(a)!{" "}
                        <span role="img" aria-label="aceno">
                            👋
                        </span>
                    </Text>
                    <Text {...introStyles} as="p">
                        Em que podemos ajudar hoje?
                    </Text>
                    <Box {...cardsContainerStyles}>
                        <Box
                            as="button"
                            type="button"
                            data-test="pre-engagement-help-center-card"
                            onClick={() => window.open(HELP_CENTER_URL, "_blank", "noopener,noreferrer")}
                            {...cardStyles}
                        >
                            <Text as="span" {...cardTitleStyles}>
                                Central de Ajuda
                            </Text>
                            <Text as="span" {...cardSubtitleStyles}>
                                Perguntas frequentes (FAQ)
                            </Text>
                        </Box>
                    </Box>
                    <Box {...buttonContainerStyles}>
                        <Button
                            variant="primary"
                            data-test="syncia-start-service-button"
                            onClick={() => dispatch(updatePreEngagementStep(2))}
                        >
                            Iniciar Atendimento
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box as="form" data-test="pre-engagement-chat-form" onSubmit={handleSubmit} {...formStyles}>
                    <Box {...fieldStyles}>
                        <Label htmlFor="name">Nome</Label>
                        <Input
                            type="text"
                            placeholder="Digite seu nome"
                            name="name"
                            data-test="pre-engagement-chat-form-name-input"
                            value={name}
                            onChange={(e) => dispatch(updatePreEngagementData({ name: e.target.value }))}
                            maxLength={50}
                            required
                        />
                    </Box>
                    <Box {...fieldStyles}>
                        <Label htmlFor="cpf">CPF</Label>
                        <Input
                            type="text"
                            placeholder="Digite seu CPF"
                            name="cpf"
                            data-test="pre-engagement-chat-form-cpf-input"
                            value={cpf}
                            onChange={(e) => dispatch(updatePreEngagementData({ cpf: maskCpf(e.target.value) }))}
                            hasError={cpfError}
                            inputMode="numeric"
                            maxLength={14}
                            aria-describedby="cpf-error"
                            required
                        />
                        {cpfError && (
                            <HelpText id="cpf-error" variant="error">
                                CPF inválido
                            </HelpText>
                        )}
                    </Box>
                    <Box {...buttonContainerStyles}>
                        <Box
                            as="button"
                            type="button"
                            data-test="pre-engagement-back-button"
                            onClick={() => dispatch(updatePreEngagementStep(1))}
                            {...backButtonStyles}
                        >
                            <ArrowBackIcon decorative={true} size="sizeIcon30" />
                            <Text as="span" marginLeft="space20">
                                Voltar
                            </Text>
                        </Box>
                        <Button
                            variant="primary"
                            type="submit"
                            data-test="pre-engagement-start-chat-button"
                            disabled={!isFormValid}
                        >
                            Iniciar conversa
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
};
